/**
 * Trainer-Reihenfolge pro Studio (Iter 318 / Feb 2026).
 *
 * Admin-Dialog: Pro Studio eine sortierbare Liste der Trainer. Die hier
 * gespeicherte Reihenfolge wird im Kalender als Spalten-Reihenfolge
 * angewendet und ist auch via Drag&Drop direkt im Kalender-Header
 * änderbar.
 */
import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogTrigger } from '@/components/ui/dialog';
import { ArrowUp, ArrowDown, GripVertical, ListOrdered } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

export const TrainerOrderManager = () => {
  const [open, setOpen] = useState(false);
  const [studios, setStudios] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [selectedStudioId, setSelectedStudioId] = useState(null);
  const [order, setOrder] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);

  // Studios laden, sobald Dialog geöffnet wird
  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [studioRes, trainerRes] = await Promise.all([
          axios.get(`${API}/studios`, { headers: authHeaders() }),
          axios.get(`${API}/trainers/availability/all`, {
            params: { show_all: true },
            headers: authHeaders(),
          }),
        ]);
        const studioList = studioRes.data || [];
        setStudios(studioList);
        setTrainers(trainerRes.data || []);
        if (studioList.length && !selectedStudioId) {
          setSelectedStudioId(studioList[0].id);
        }
      } catch (e) {
        console.error(e);
        toast.error('Konnte Studios/Trainer nicht laden');
      }
    })();
  }, [open]);

  // Order für ausgewähltes Studio laden
  useEffect(() => {
    if (!open || !selectedStudioId) return;
    setLoading(true);
    axios
      .get(`${API}/trainers/order`, {
        params: { studio_id: selectedStudioId },
        headers: authHeaders(),
      })
      .then((res) => setOrder((res.data && res.data.order) || []))
      .catch(() => setOrder([]))
      .finally(() => setLoading(false));
  }, [open, selectedStudioId]);

  // Trainer dieses Studios bestimmen + in gespeicherter Reihenfolge sortieren.
  // Trainer ohne Studio-Zuordnung (alte Datensätze) zeigen wir trotzdem.
  const trainersForStudio = useMemo(() => {
    if (!selectedStudioId) return [];
    const matching = trainers.filter((td) => {
      const profileStudios = td.trainer.studio_ids || [];
      const av = td.availability || {};
      const DAYS = ['monday','tuesday','wednesday','thursday','friday','saturday','sunday'];
      const slotStudios = DAYS.flatMap((d) => ((av[d]?.slots) || []).map((s) => s.studio_id).filter(Boolean));
      const set = new Set([...profileStudios, ...slotStudios]);
      if (set.size === 0) return true; // unconfigured – in allen sichtbar
      return set.has(selectedStudioId);
    });

    const indexOf = (id) => {
      const i = order.indexOf(id);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return [...matching].sort((a, b) => {
      const ai = indexOf(a.trainer.id);
      const bi = indexOf(b.trainer.id);
      if (ai !== bi) return ai - bi;
      const an = `${a.trainer.first_name || ''} ${a.trainer.last_name || ''}`.toLowerCase();
      const bn = `${b.trainer.first_name || ''} ${b.trainer.last_name || ''}`.toLowerCase();
      return an.localeCompare(bn);
    });
  }, [trainers, selectedStudioId, order]);

  const persistOrder = async (newOrder) => {
    if (!selectedStudioId) return;
    setSaving(true);
    try {
      await axios.put(
        `${API}/trainers/order`,
        { studio_id: selectedStudioId, order: newOrder },
        { headers: authHeaders() },
      );
      setOrder(newOrder);
      toast.success('Reihenfolge gespeichert');
    } catch (e) {
      console.error(e);
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const moveTrainer = (trainerId, direction) => {
    const ids = trainersForStudio.map((t) => t.trainer.id);
    const idx = ids.indexOf(trainerId);
    const newIdx = idx + direction;
    if (idx < 0 || newIdx < 0 || newIdx >= ids.length) return;
    const next = [...ids];
    [next[idx], next[newIdx]] = [next[newIdx], next[idx]];
    persistOrder(next);
  };

  const handleDrop = (targetId) => {
    if (!dragId || dragId === targetId) {
      setDragId(null);
      setDragOverId(null);
      return;
    }
    const ids = trainersForStudio.map((t) => t.trainer.id);
    const from = ids.indexOf(dragId);
    const to = ids.indexOf(targetId);
    if (from === -1 || to === -1) return;
    const next = [...ids];
    next.splice(from, 1);
    next.splice(to, 0, dragId);
    setDragId(null);
    setDragOverId(null);
    persistOrder(next);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          data-testid="open-trainer-order-dialog"
          className="gap-2"
        >
          <ListOrdered size={16} />
          Kalender-Reihenfolge
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Kalender-Reihenfolge der Trainer</DialogTitle>
          <DialogDescription>
            Pro Studio festlegen, in welcher Reihenfolge Trainer-Spalten im Kalender erscheinen.
            Änderungen werden sofort gespeichert.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Studio-Switcher */}
          <div className="flex flex-wrap gap-2">
            {studios.map((s) => (
              <button
                key={s.id}
                type="button"
                data-testid={`trainer-order-studio-${s.short_name || s.id}`}
                onClick={() => setSelectedStudioId(s.id)}
                className={`px-3 py-1.5 rounded-full text-sm font-medium border-2 transition ${
                  selectedStudioId === s.id
                    ? 'bg-teal-600 text-white border-teal-700'
                    : 'bg-white text-gray-700 border-gray-200 hover:border-teal-400'
                }`}
              >
                📍 {s.short_name || s.name}
              </button>
            ))}
          </div>

          {/* Trainer-Liste */}
          {loading ? (
            <div className="text-center py-8 text-gray-500 text-sm">Lade…</div>
          ) : trainersForStudio.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              Keine Trainer für dieses Studio.
            </div>
          ) : (
            <ul className="border rounded-lg divide-y bg-white" data-testid="trainer-order-list">
              {trainersForStudio.map((td, idx) => {
                const isDragOver = dragOverId === td.trainer.id && dragId && dragId !== td.trainer.id;
                return (
                  <li
                    key={td.trainer.id}
                    draggable
                    onDragStart={(e) => {
                      setDragId(td.trainer.id);
                      try { e.dataTransfer.effectAllowed = 'move'; } catch { /* noop */ }
                    }}
                    onDragOver={(e) => {
                      if (!dragId) return;
                      e.preventDefault();
                      if (dragOverId !== td.trainer.id) setDragOverId(td.trainer.id);
                    }}
                    onDragLeave={() => {
                      if (dragOverId === td.trainer.id) setDragOverId(null);
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      handleDrop(td.trainer.id);
                    }}
                    onDragEnd={() => { setDragId(null); setDragOverId(null); }}
                    data-testid={`trainer-order-row-${td.trainer.id}`}
                    className={`flex items-center gap-3 px-3 py-2 transition ${
                      dragId === td.trainer.id ? 'opacity-40' : ''
                    } ${isDragOver ? 'bg-teal-50 ring-2 ring-teal-400' : ''}`}
                  >
                    <GripVertical size={16} className="text-gray-400 cursor-move" />
                    <span className="text-sm font-medium tabular-nums text-gray-500 w-6">
                      {idx + 1}.
                    </span>
                    <div className="flex-1">
                      <div className="text-sm font-medium">
                        {td.trainer.first_name} {td.trainer.last_name}
                      </div>
                      <div className="text-xs text-gray-500">{td.trainer.role}</div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={idx === 0 || saving}
                      onClick={() => moveTrainer(td.trainer.id, -1)}
                      data-testid={`trainer-order-up-${td.trainer.id}`}
                      aria-label="Nach oben"
                    >
                      <ArrowUp size={14} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={idx === trainersForStudio.length - 1 || saving}
                      onClick={() => moveTrainer(td.trainer.id, 1)}
                      data-testid={`trainer-order-down-${td.trainer.id}`}
                      aria-label="Nach unten"
                    >
                      <ArrowDown size={14} />
                    </Button>
                  </li>
                );
              })}
            </ul>
          )}

          <p className="text-xs text-gray-500">
            Tipp: Du kannst die Reihenfolge auch direkt im Kalender per Drag&Drop der Spalten-Header ändern.
          </p>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default TrainerOrderManager;
